import { html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import style from './style/zaptic-tile-style'
import classNames from 'classnames'
import type { IconName } from './helpers/icons'

export enum Events {
    onExtraAction = 'onExtraAction',
    onClickEditButton = 'onClickEditButton',
    onClickDeleteButton = 'onClickDeleteButton',
    onSelectionChange = 'onSelectionChange',
}

export interface CustomEventPayload<Id = any> {
    [Events.onExtraAction]: Id
    [Events.onClickEditButton]: Id
    [Events.onClickDeleteButton]: Id
    [Events.onSelectionChange]: boolean
}

type ShowSelectionCheckboxType = 'always' | 'never' | 'on-hover'
const checkboxClassLookup: Record<ShowSelectionCheckboxType, string> = {
    always: 'checkbox-show-always',
    'on-hover': 'checkbox-show-on-hover',
    never: 'checkbox-show-never',
}

@customElement('zaptic-tile')
export class Tile<Id = any> extends LitElement {
    static styles = style

    // Mandatory properties
    @property()
    entityId: Id
    @property()
    icon: string
    @property()
    title: string
    @property()
    subtitle: string

    // Optional properties
    @property()
    fallbackIcon?: string
    @property()
    showSelectionCheckbox: 'always' | 'never' | 'on-hover' = 'never'
    @property()
    extraActionIcon?: string
    @property()
    extraActionHoverIcon?: string
    @property({ type: Boolean })
    selected: boolean
    @property({ type: Boolean })
    preview: boolean = false
    @property({ type: Boolean })
    extraActionIconLarge: boolean
    @property({ type: Boolean })
    disablePropagationOnExtraActionIconClick: boolean
    @property()
    deleteIconName?: IconName
    @property()
    editIconName?: IconName
    @property()
    summaryIcon?: IconName
    @property({ type: Boolean })
    isVideo?: boolean
    @property()
    snippet?: string
    @property()
    number?: number | string

    @state()
    private backgroundImageUrl: string = 'none'
    @state()
    private isInFallbackMode = false

    onCheckboxChange(event: MouseEvent) {
        const customEvent = new CustomEvent<CustomEventPayload[Events.onSelectionChange]>(Events.onSelectionChange, {
            detail: (event.target as HTMLInputElement).checked,
        })

        this.dispatchEvent(customEvent)
    }

    onCheckboxClick(event: MouseEvent) {
        // prevent the tile itself being clicked:
        event.stopPropagation()
    }

    onClickEditButton(event: MouseEvent) {
        event.stopPropagation()
        event.preventDefault()

        // Dispatch event the click event on the edit button
        const customEvent = new CustomEvent(Events.onClickEditButton, { detail: this.entityId, composed: true })
        this.dispatchEvent(customEvent)
    }

    onClickDeleteButton(event: MouseEvent) {
        event.stopPropagation()
        event.preventDefault()

        // Dispatch event the click event on the delete button
        const customEvent = new CustomEvent(Events.onClickDeleteButton, { detail: this.entityId, composed: true })
        this.dispatchEvent(customEvent)
    }

    updateBackgroundImage(url: string) {
        this.backgroundImageUrl = `url(${url})`
    }

    protected firstUpdated(): void {
        const backgroundImage = this.icon
        const fallbackBackgroundImage = this.fallbackIcon ?? null

        const image = new Image()

        // Use the icon as the cover image by default
        image.onload = () => this.updateBackgroundImage(backgroundImage)

        // If the icon fails to load, switch to the fallback icon
        image.onerror = () => {
            if (!this.fallbackIcon) {
                return
            }
            this.isInFallbackMode = true
            this.updateBackgroundImage(fallbackBackgroundImage)
        }

        // This will initiate background image fetching
        image.src = backgroundImage
    }

    render() {
        this.ariaLabel = this.title

        // There are two cases where we do not want to render the icon as a 'cover' image:
        // - If provided icon is an SVG
        // - If the icon failed to load, and the fallback icon is an SVG

        const shouldRenderCover = this.isInFallbackMode
            ? !this.fallbackIcon?.endsWith('.svg')
            : !this.icon?.endsWith('.svg')

        const iconClasses = classNames('icon', 'main-icon', { 'icon-cover': shouldRenderCover })
        const classes = classNames('container', { selected: this.selected, preview: this.preview })
        const checkboxClasses = classNames('checkbox-wrapper', checkboxClassLookup[this.showSelectionCheckbox])

        return html`
            <div class="${classes}">
                <div class="content">
                    ${this.number !== undefined ? html`<div class="number">${this.number}</div>` : nothing}
                    <slot name="content">
                        <div class=${iconClasses} style="background-image: ${this.backgroundImageUrl}">
                            <slot name="icon"></slot>
                        </div>
                        <label class=${checkboxClasses} @click=${this.onCheckboxClick}>
                            <input type="checkbox" .checked=${this.selected} @change=${this.onCheckboxChange} />
                        </label>
                        <div class="action-buttons">${this.renderEditIcon()} ${this.renderDeleteIcon()}</div>
                        ${this.renderVideoOverlay()}
                    </slot>
                </div>
                <div class="summary summary-size">
                    <div class="summary-item main-title">
                        <zaptic-ellipsis class="text-overflow">${this.title}</zaptic-ellipsis>
                        ${this.renderSummaryIcon()}
                    </div>
                    <div>
                        <slot name="parentInfo"></slot>
                    </div>
                    ${this.renderSnippet()} ${this.renderSubtitle()}
                </div>
                <slot name="right-content"></slot>
                ${this.renderExtraAction()}
            </div>
        `
    }

    private renderEditIcon() {
        if (!this.editIconName) {
            return html``
        }

        return html`<button class="edit-icon" @click="${this.onClickEditButton}">
            <zaptic-icon icon-name="${this.editIconName}"></zaptic-icon>
        </button>`
    }

    private renderDeleteIcon() {
        if (!this.deleteIconName) {
            return html``
        }

        return html`<button class="delete-icon" @click="${this.onClickDeleteButton}">
            <zaptic-icon icon-name="${this.deleteIconName}"></zaptic-icon>
        </button>`
    }

    private renderSubtitle() {
        if (!this.subtitle) {
            return html``
        }

        return html`
            <div class="summary-item">
                <div class="title subtitle">
                    <zaptic-ellipsis class="text-overflow">${this.subtitle}</zaptic-ellipsis>
                </div>
            </div>
        `
    }

    private renderExtraAction() {
        if (!this.extraActionIcon) {
            return null
        }

        const hoverIcon = !!this.extraActionHoverIcon ? this.extraActionHoverIcon : this.extraActionIcon

        return html`
            <div class="extra-action extra-action-margin">
                <button
                    class=${classNames(
                        'extra-action-btn',
                        'extra-action-btn-size',
                        this.extraActionIconLarge ? 'large' : ''
                    )}
                    @click=${this.dispatchExtraActionEvent}
                >
                    <img class="not-hover" src="${this.extraActionIcon}" />
                    <img class="hover" src="${hoverIcon}" />
                </button>
            </div>
        `
    }

    private renderSummaryIcon() {
        return this.summaryIcon
            ? html`<zaptic-icon icon-name="${this.summaryIcon}" class="summary-icon"></zaptic-icon>`
            : html``
    }

    private renderVideoOverlay() {
        return this.isVideo
            ? html`<div class="video-overlay"><zaptic-icon icon-name="play-video"></zaptic-icon></div>`
            : html``
    }

    private renderSnippet() {
        if (this.preview) {
            return this.snippet?.length
                ? html`<div class="snippet">&ldquo;${this.snippet}&rdquo;</div>`
                : html`<div class="no-snippet"></div>`
        }
    }

    private dispatchExtraActionEvent(e: Event) {
        this.dispatchEvent(
            new CustomEvent<CustomEventPayload<Id>['onExtraAction']>(Events.onExtraAction, {
                detail: this.entityId,
            })
        )
        if (this.disablePropagationOnExtraActionIconClick) {
            e.stopPropagation()
        }
    }
}
